import type { RefObject } from "react";
import { Product, Store } from "../../../__generated__/graphql";
import {
  canProductExpose,
  canPackExpose,
  getExposeType,
} from "../../../utils/store-expose";
import {
  getReserveTime,
  getReserveTimeForTomorrow,
} from "../../../utils/reserve-time";
import { track } from "@amplitude/analytics-browser";
import { isApp, isWeb } from "../../../utils/user-agent";
import { getDownloadLink } from "../../../utils/app-download-link";
import Logo from "../../../assets/images/mahi-download-logo-512.png";
import { StoreCardForPack } from "../../../components/store-card/store-card-for-pack";
import { StoreCardForTimesail } from "../../../components/store-card/store-card-for-timesail";
import { StoreCardForTimesailYet } from "../../../components/store-card/store-card-for-timesail-yet";
import type { StoreCoupon } from "../../../components/store-card/types";
import BusinessInfoWhiteBg from "../../../components/common/business-info-white-bg";
import { HiStar } from "react-icons/hi2";

/**
 * 홈 매장 리스트: 필터된 매장을 실시간팩/예약팩/기본 카드로 렌더링,
 * 무한 스크롤 트리거, 앱 CTA, 웹 하단 앱 다운로드 배너, 사업자 정보 포함.
 */
export interface HomeStoreListProps {
  filteredStores: Store[];
  attachObserver: (el: HTMLElement | null, storeId: number) => void;
  loadMoreRef: RefObject<HTMLDivElement>;
  availableFilter: string;
  hasMore: boolean;
  isLoadingMore: boolean;
  onClickStore: (store: Store, type: string) => void;
  onClickLike: (
    storeId: number,
    isLiked: boolean | null,
    storeTitle?: string,
  ) => Promise<void>;
  onOpenSheet: () => void;
  navigate: (path: string) => void;
}

export function HomeStoreList({
  filteredStores,
  attachObserver,
  loadMoreRef,
  availableFilter,
  hasMore,
  isLoadingMore,
  onClickStore,
  onClickLike,
  onOpenSheet,
  navigate,
}: HomeStoreListProps) {
  return (
    <div className="px-4 pt-4 pb-8 mb-16">
      {/* 매장 리스트: 실시간팩(Timesail) / 예약팩(Pack) / 기본(TimesailYet) 카드 분기 */}
      {filteredStores.map((store) => {
        if (canProductExpose(store)) {
          return (
            <div
              className="mb-4"
              key={store.id}
              ref={(el) => attachObserver(el, store.id)}
            >
              <StoreCardForTimesail
                title={store.title as string}
                products={store.todaysProducts as Product[]}
                closingHours={store.closingHours as string}
                discountPrice={
                  (store.todaysProducts as Product[])[0]?.userPrice
                }
                price={(store.todaysProducts as Product[])[0]?.price}
                distance={store.distance}
                img={store.img as string}
                onClick={() => onClickStore(store, "실시간팩")}
                likeCount={store?.likes?.length ?? 0}
                signupDate={store.createdAt}
                availableStoreCoupon={store.availableStoreCoupon as StoreCoupon | undefined}
              />
            </div>
          );
        }
        if (store.pack && canPackExpose(store)) {
          return (
            <div
              className="mb-4"
              key={store.id}
              ref={(el) => attachObserver(el, store.id)}
            >
              <StoreCardForPack
                title={store.title}
                quantity={store.pack.currentCount}
                discountPrice={store.pack.userPrice}
                price={store.pack.price}
                isLiked={store.isLiked}
                distance={store.distance}
                img={store.img as string}
                reserveTime={getReserveTime(store.reserveTime)}
                tomorrowReserveTime={getReserveTimeForTomorrow(
                  store.reserveTime,
                )}
                onClick={() => onClickStore(store, "예약팩")}
                onClickHeart={async (e) => {
                  e.stopPropagation();
                  await onClickLike(
                    store.id,
                    store.isLiked ?? null,
                    store.title ?? undefined,
                  );
                }}
                likeCount={store?.likes?.length ?? 0}
                exposeType={getExposeType(store.pack)}
                bestReview={store.bestReview}
                signupDate={store.createdAt}
                availableStoreCoupon={store.availableStoreCoupon as StoreCoupon | undefined}
              />
            </div>
          );
        }
        return (
          <div
            className="mb-4"
            key={store.id}
            ref={(el) => attachObserver(el, store.id)}
          >
            <StoreCardForTimesailYet
              title={store.title as string}
              closingHours={store.closingHours as string}
              distance={store.distance}
              img={store.img as string}
              quantity={0}
              onClick={() => onClickStore(store, "기본")}
              isLiked={store.isLiked}
              onClickHeart={async (e) => {
                e.stopPropagation();
                await onClickLike(
                  store.id,
                  store.isLiked ?? null,
                  store.title ?? undefined,
                );
              }}
              likeCount={store?.likes?.length ?? 0}
              signupDate={store.createdAt}
              availableStoreCoupon={store.availableStoreCoupon as StoreCoupon | undefined}
            />
          </div>
        );
      })}

      {/* 무한 스크롤을 위한 observer 요소 (바로픽업 필터가 아닐 때만) */}
      {availableFilter !== "PICKUP_NOW" && hasMore && (
        <div
          ref={loadMoreRef}
          className="flex justify-center items-center py-8 px-4"
        >
          {isLoadingMore && (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#1562fc]" />
              <span className="text-gray-600 text-sm">불러오는 중...</span>
            </div>
          )}
        </div>
      )}

      {/* 앱 전용: 매장 추천하기 / 개발자와 소통하기 */}
      {isApp() && (
        <div className="w-full mx-auto mt-8 mb-14 rounded-md flex justify-center items-center cursor-pointer">
          <div className="space-x-4 grid grid-cols-2 w-full">
            <a
              href="https://forms.gle/PXmEvzq4LsNDC1Gj9"
              className="text-black text-center text-lg font-semibold bg-gray-200 py-3 rounded-xl transition"
            >
              매장 추천하기
            </a>
            <button
              onClick={() => navigate("/community")}
              className="text-black text-center text-lg font-semibold bg-gray-200 py-3 rounded-xl transition"
            >
              개발자와 소통하기
            </button>
          </div>
        </div>
      )}

      {/* 앱 다운로드 하단 배너 (웹에서만) */}
      {isWeb() && (
        <div className="mb-12 w-full flex flex-col justify-center items-center py-4">
          <img src={Logo} className="w-24 h-24" alt="App Logo" />
          <div className="flex items-center gap-1 mt-2">
            <HiStar className="text-yellow-400 text-lg" />
            <span className="text-lg font-bold text-[#1562fc]">4.8</span>
          </div>
          <p className="mt-1 text-lg font-extrabold text-center text-gray-800">
            앱에서 내 근처 매장 <br /> 마감할인 알림 받기
          </p>
          <a
            href={getDownloadLink()}
            className="mt-4 px-6 py-3 bg-[#1692fc] text-white font-bold rounded-full shadow-lg hover:bg-[#1177cc] transition-all duration-200"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              track("앱 다운로드 클릭", { 어디서: "홈 하단 배너" });
            }}
          >
            앱 다운로드
          </a>
        </div>
      )}

      {/* 사업자 정보 */}
      <BusinessInfoWhiteBg />
    </div>
  );
}
